(function () {
  if (window.__productAccordionMotionLoaded) return;
  window.__productAccordionMotionLoaded = true;

  const ACCORDION_SELECTOR = '.product__accordion details';
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new WeakMap();

  function escapeAttributeValue(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }

    return value.replace(/"/g, '\\"');
  }

  class ProductAccordion {
    constructor(details) {
      this.details = details;
      this.summary = details.querySelector(':scope > summary');
      this.content = details.querySelector(':scope > .accordion__content');
      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.transitionToken = 0;
      this.activeTransitionHandler = null;
      this.isAnimating = false;
      this.groupName = this.details.dataset.accordionGroup || this.details.getAttribute('name') || '';
      this.resizeObserver = window.ResizeObserver ? new ResizeObserver(() => this.onContentResize()) : null;
      this.baseMarginBottom = '0px';

      if (!this.summary || !this.content) return;

      if (this.groupName) {
        this.details.dataset.accordionGroup = this.groupName;
        this.details.removeAttribute('name');
      }

      this.baseMarginBottom = window.getComputedStyle(this.content).marginBottom;
      this.content.style.overflow = 'hidden';
      this.content.style.transition = 'max-height 0.4s ease-out, opacity 0.25s ease, margin-bottom 0.4s ease-out';

      if (this.details.open) {
        this.content.style.maxHeight = `${this.getExpandedHeight()}px`;
        this.content.style.opacity = '1';
        this.content.style.marginBottom = this.baseMarginBottom;
      } else {
        this.content.style.maxHeight = '0px';
        this.content.style.opacity = '0';
        this.content.style.marginBottom = '0px';
      }

      this.resizeObserver?.observe(this.content);
      this.summary.addEventListener('click', this.onSummaryClick);
    }

    destroy() {
      this.summary?.removeEventListener('click', this.onSummaryClick);
      this.clearTransitionHandler();
      this.resizeObserver?.disconnect();
    }

    onSummaryClick(event) {
      if (!this.content) return;
      if (prefersReducedMotion.matches) return;

      event.preventDefault();

      if (this.details.open) {
        this.close();
      } else {
        this.closeSameGroupSiblings();
        this.open();
      }
    }

    open() {
      if (!this.content) return;

      const token = ++this.transitionToken;
      this.clearTransitionHandler();
      this.isAnimating = true;

      this.details.open = true;
      this.content.style.maxHeight = '0px';
      this.content.style.opacity = '0';
      this.content.style.marginBottom = '0px';

      window.requestAnimationFrame(() => {
        if (!this.content || !this.details.open) return;

        this.content.style.maxHeight = `${this.getExpandedHeight()}px`;
        this.content.style.opacity = '1';
        this.content.style.marginBottom = this.baseMarginBottom;
      });

      const onTransitionEnd = (event) => {
        if (event.target !== this.content || event.propertyName !== 'max-height') return;
        if (token !== this.transitionToken) return;
        this.clearTransitionHandler();
        this.isAnimating = false;

        if (this.details.open) {
          this.content.style.maxHeight = `${this.getExpandedHeight()}px`;
        }
      };

      this.activeTransitionHandler = onTransitionEnd;
      this.content.addEventListener('transitionend', onTransitionEnd);
    }

    close() {
      if (!this.content) return;

      const token = ++this.transitionToken;
      this.clearTransitionHandler();
      this.isAnimating = true;

      this.content.style.maxHeight = `${this.getExpandedHeight()}px`;
      this.content.style.opacity = '1';
      this.content.style.marginBottom = this.baseMarginBottom;

      window.requestAnimationFrame(() => {
        if (!this.content) return;
        this.content.style.maxHeight = '0px';
        this.content.style.opacity = '0';
        this.content.style.marginBottom = '0px';
      });

      const onTransitionEnd = (event) => {
        if (event.target !== this.content || event.propertyName !== 'max-height') return;
        if (token !== this.transitionToken) return;
        this.clearTransitionHandler();
        this.isAnimating = false;

        this.details.open = false;
        this.content.style.maxHeight = '0px';
        this.content.style.opacity = '0';
        this.content.style.marginBottom = '0px';
      };

      this.activeTransitionHandler = onTransitionEnd;
      this.content.addEventListener('transitionend', onTransitionEnd);
    }

    closeSameGroupSiblings() {
      if (!this.groupName) return;

      const escaped = escapeAttributeValue(this.groupName);
      const root = this.details.getRootNode();
      if (!('querySelectorAll' in root)) return;

      root.querySelectorAll(`${ACCORDION_SELECTOR}[data-accordion-group="${escaped}"]`).forEach((otherDetails) => {
        if (!(otherDetails instanceof HTMLDetailsElement)) return;
        if (otherDetails === this.details || !otherDetails.open) return;

        const sibling = instances.get(otherDetails);
        if (sibling) {
          sibling.close();
        } else {
          otherDetails.open = false;
        }
      });
    }

    clearTransitionHandler() {
      if (!this.content || !this.activeTransitionHandler) return;
      this.content.removeEventListener('transitionend', this.activeTransitionHandler);
      this.activeTransitionHandler = null;
    }

    getExpandedHeight() {
      return this.content ? this.content.scrollHeight : 0;
    }

    onContentResize() {
      if (!this.content) return;
      if (this.isAnimating) return;
      if (!this.details.open) return;

      this.content.style.maxHeight = `${this.getExpandedHeight()}px`;
      this.content.style.marginBottom = this.baseMarginBottom;
    }
  }

  function initAccordions(scope = document) {
    const root = scope instanceof Document || scope instanceof Element ? scope : document;

    root.querySelectorAll(ACCORDION_SELECTOR).forEach((details) => {
      if (instances.has(details)) return;
      instances.set(details, new ProductAccordion(details));
    });
  }

  function cleanupAccordions(scope) {
    if (!(scope instanceof Element)) return;

    scope.querySelectorAll(ACCORDION_SELECTOR).forEach((details) => {
      const instance = instances.get(details);
      if (!instance) return;
      instance.destroy();
      instances.delete(details);
    });
  }

  document.addEventListener('shopify:section:load', (event) => initAccordions(event.target));
  document.addEventListener('shopify:section:select', (event) => initAccordions(event.target));
  document.addEventListener('shopify:section:reorder', (event) => initAccordions(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupAccordions(event.target));
  document.addEventListener('product-info:loaded', (event) => initAccordions(event.target));
  document.addEventListener('DOMContentLoaded', () => initAccordions(document));
  window.addEventListener('load', () => initAccordions(document));

  initAccordions(document);
})();
